import { decodeEntities, GROUP_RECORD_ID, MAX_REPEAT_CARDS, TEMPLATE_RECORD_ID } from "./bundles";
import { bundleMarkedElements, REVIEW_MEDIA_PATH } from "./presentation";
import { previousShopTemplateFiles } from "./modules/shop/templates";
const NODE_TYPES = ["header", "footer", "nav", "section", "heading", "text", "image", "link", "repeat", "card", "control"];
const ATTRIBUTES = ["href", "src", "alt", "title", "aria-label"];
const MAX_NODE_RECORDS = 32;
/** The type a plain tag implies. Anything else is a `section` guessed from an ordinary element. */
const TAG_TYPES = {
    header: "header", footer: "footer", nav: "nav", section: "section", main: "section",
    h1: "heading", h2: "heading", h3: "heading", h4: "heading", h5: "heading", h6: "heading",
    img: "image", a: "link", button: "control", form: "control", input: "control",
};
/** A tag whose type says more than "it repeats": its children never turn it into a `repeat`. */
const SEMANTIC_TAGS = new Set(["header", "footer", "nav", "h1", "h2", "h3", "h4", "h5", "h6", "img", "a", "button", "form", "input"]);
/** Record kinds that make up lists: sibling nodes showing only these, of one kind, are repeated items. */
const LIST_KINDS = new Set(["service", "project", "item", "faq", "link", "social", "media"]);
/** The record id a template marker writes for the member a repeat is showing (`item.title`); stored bindings call it `@item`. */
const ITEM_MARKER = "item";
const EMPTY_MARKER = /\sdata-stackje-empty(?=[\s=>/])/;
/**
 * The fields each list kind has, optional ones included, so a template can be checked against a kind
 * rather than against whichever members exist. Kinds not listed are checked on their records.
 */
const KIND_FIELDS = {
    service: ["title", "description", "image", "url"], project: ["title", "description", "image", "url"],
    item: ["title", "text", "detail", "image", "url"], faq: ["question", "answer"], link: ["label", "url"],
    social: ["platform", "url"], copy: ["title", "body"], profile: ["name", "bio"],
    product: ["name", "title", "price", "compareAt", "photo", "url", "status", "description"],
    post: ["title", "summary", "cover", "url", "date", "readingTime", "tags", "body"],
};
/** An empty collection has no kinds to check: a template may use what any of the item kinds has. */
const EMPTY_COLLECTION_FIELDS = [...new Set(["service", "project", "item"].flatMap((kind) => KIND_FIELDS[kind]))];
const MARKER = /^([A-Za-z][A-Za-z0-9_-]{0,63})(?:\.([A-Za-z][A-Za-z0-9_]{0,63}))?(?:@(href|src|alt|title|aria-label))?$/;
/** Reads a `data-stackje-bind` value as a content marker, or `null` when it isn't one (an opaque id such as `hero_title` parses as a bare record id). */
export function parseContentMarker(value) {
    const match = MARKER.exec(value);
    if (!match)
        return null;
    return { recordId: match[1], ...(match[2] ? { field: match[2] } : {}), ...(match[3] ? { attribute: match[3] } : {}) };
}
function attributeValue(source, name) {
    const match = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, "i").exec(source);
    return match ? match[1] ?? match[2] ?? "" : undefined;
}
function isHtml(path) {
    return path.toLowerCase().endsWith(".html");
}
function fileStem(path) {
    return path.replace(/\.html$/i, "");
}
/** Keeps an id inside the identifier charset the schema allows: a letter first, then letters, digits, `_` and `-`, at most 64. */
function sanitizeId(text) {
    const cleaned = text.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^[^A-Za-z]+/, "");
    return (cleaned || "b").slice(0, 64);
}
function uniqueId(base, used) {
    const clean = sanitizeId(base);
    for (let n = 1;; n += 1) {
        const suffix = n === 1 ? "" : `-${n}`;
        const id = `${clean.slice(0, 64 - suffix.length)}${suffix}`;
        if (!used.has(id)) {
            used.add(id);
            return id;
        }
    }
}
function isItemRecord(record) {
    return record?.kind === "service" || record?.kind === "project" || record?.kind === "item";
}
/** Routes: the previous manifest's where their file still exists, plus `/<stem>` for each new page (`index.html` is `/`, `404.html` has none). */
function generateRoutes(htmlFiles, previous, unresolved, itemPages, content, shopWork = false, shopLists = []) {
    const present = new Set(htmlFiles);
    const routes = {};
    const previousBase = Object.keys(previous?.routes ?? {}).find((path) => path.endsWith("/{slug}") && !Object.keys(previous?.routes ?? {}).some((other) => other.startsWith(`${path.slice(0, -"/{slug}".length)}/c/{`)))?.slice(0, -"/{slug}".length);
    for (const [path, file] of Object.entries(previous?.routes ?? {}))
        if (present.has(file) && !(previousBase && content.modules?.blog && previousBase !== content.modules.blog.base && (path === previousBase || path.startsWith(`${previousBase}/`))))
            routes[path] = file;
    const routed = new Set(Object.values(routes));
    for (const file of htmlFiles) {
        if (file === "404.html" || routed.has(file) || itemPages[file])
            continue;
        const stem = fileStem(file);
        const path = stem === "index" ? "/" : `/${stem.replace(/\/index$/, "")}`;
        if (Object.hasOwn(routes, path)) {
            unresolved.push({ file, reason: `route ${path} already points at ${routes[path]}` });
            continue;
        }
        routes[path] = file;
    }
    const blog = content.modules?.blog;
    if (blog && blog.enabled !== false) {
        const list = routes[blog.base] ?? (htmlFiles.includes("journal.html") ? "journal.html" : undefined);
        const item = Object.entries(itemPages).find(([, collection]) => collection === "posts")?.[0];
        if (list) {
            // The Journal is served at the blog's address only, not also at the one its file name would give it.
            for (const [path, file] of Object.entries(routes))
                if (file === list && path !== blog.base && !path.includes("{"))
                    delete routes[path];
            routes[blog.base] = list;
            routes[`${blog.base}/page/{n}`] = list;
            routes[`${blog.base}/tag/{tag}`] = list;
            routes[`${blog.base}/tag/{tag}/page/{n}`] = list;
        }
        if (item)
            routes[`${blog.base}/{slug}`] = item;
    }
    const shop = content.modules?.shop;
    if (shop && shop.enabled !== false && (shopWork || Object.values(itemPages).includes("products"))) {
        const oldShopFiles = previousShopTemplateFiles(previous);
        const websiteFiles = new Set(Object.entries(previous?.routes ?? {}).filter(([path, file]) => path !== shop.base && !path.startsWith(`${shop.base}/`) && !oldShopFiles.has(file)).map(([, file]) => file));
        const list = routes[shop.base] ?? ["shop-list.html", "shop.html", ...shopLists.filter(file => file !== "category.html")].find(file => present.has(file) && !websiteFiles.has(file));
        const item = Object.entries(itemPages).find(([, collection]) => collection === "products")?.[0];
        if (list) {
            for (const [path, file] of Object.entries(routes))
                if (file === list && !path.includes("{"))
                    delete routes[path];
            routes[shop.base] = list;
            routes[`${shop.base}/page/{n}`] = list;
            routes[`${shop.base}/c/{slug}`] = htmlFiles.includes("category.html") ? "category.html" : list;
            routes[`${shop.base}/c/{slug}/page/{n}`] = routes[`${shop.base}/c/{slug}`];
            if (htmlFiles.includes("category.html"))
                delete routes["/category"];
        }
        if (item)
            routes[`${shop.base}/{slug}`] = item;
    }
    return routes;
}
/** Whether a field exists on every kind of record `records` holds (any item kind's field when there are none). */
function everyKindHasField(records, field, kind) {
    if ((field === "body" && kind === "post") || (field === "description" && kind === "product"))
        return false;
    if (!records.length)
        return kind && KIND_FIELDS[kind] ? KIND_FIELDS[kind].includes(field) : EMPTY_COLLECTION_FIELDS.includes(field);
    return [...new Set(records.map((record) => record.kind))].every((kind) => {
        const known = KIND_FIELDS[kind];
        return known ? known.includes(field) : records.filter((record) => record.kind === kind).every((record) => hasField(record, field));
    });
}
/** A whole-number attribute (`value` unset when absent), or the reason it can't be used. */
function repeatNumber(source, name, min, max) {
    const text = attributeValue(source, name);
    if (text === undefined)
        return {};
    const value = /^\d{1,7}$/.test(text.trim()) ? Number(text) : NaN;
    return value >= min && value <= max ? { value } : { problem: `${name}="${text}" must be a whole number from ${min} to ${max}` };
}
/**
 * The repeats of one page: each `data-stackje-repeat` container, checked, with its single template
 * child. A container with a problem is reported (and gets no repeat) but still marks its template, so
 * the `item.*` markers inside aren't blamed again.
 */
function fileRepeats(file, html, containers, content, usedRepeats, unresolved) {
    const spans = [];
    const repeats = [];
    const stem = fileStem(file);
    const marked = (html.match(/\sdata-stackje-repeat\s*=/g) ?? []).length;
    if (marked !== containers.length)
        unresolved.push({ file, repeat: true, container: "", reason: "data-stackje-repeat must name a collection id, in quotes, on an element that has a closing tag" });
    for (const container of containers) {
        const name = container.repeatId;
        const problems = [];
        const collection = Object.hasOwn(content.collections, name) ? content.collections[name] : undefined;
        if (!collection)
            problems.push(`there is no collection ${name}`);
        const templates = container.children.filter((child) => !EMPTY_MARKER.test(child.source));
        if (container.end < 0)
            problems.push("the container has no closing tag");
        else if (container.children.some((child) => child.end < 0))
            problems.push("a child element has no closing tag");
        else if (templates.length !== 1)
            problems.push(`it needs exactly one template child element (the one repeated for each member; anything else goes in data-stackje-empty), found ${templates.length}`);
        if (containers.some((outer) => outer !== container && outer.end >= 0 && outer.openEnd <= container.start && container.start < outer.closeStart))
            problems.push("a repeat can't sit inside another repeat");
        const opening = html.slice(container.start, container.openEnd);
        const start = repeatNumber(opening, "data-stackje-repeat-start", 0, 1_000_000);
        const limit = repeatNumber(opening, "data-stackje-repeat-limit", 1, MAX_REPEAT_CARDS);
        for (const problem of [start.problem, limit.problem])
            if (problem)
                problems.push(problem);
        for (const problem of problems)
            unresolved.push({ file, repeat: true, container: name, reason: problem });
        const template = templates.length === 1 && container.end >= 0 ? templates[0] : undefined;
        const records = (collection?.memberIds ?? []).map((id) => content.recordsById[id]).filter((record) => record !== undefined);
        const repeat = problems.length ? undefined : { id: uniqueId(`${stem}-${name}`, usedRepeats), file, collection: name, ...(start.value !== undefined ? { start: start.value } : {}), ...(limit.value !== undefined ? { limit: limit.value } : {}), ...(/\sdata-stackje-paginate(?=[\s=>/])/.test(opening) ? { paginate: true } : {}) };
        if (repeat)
            repeats.push(repeat);
        if (template && template.end >= 0)
            spans.push({ start: template.start, end: template.end, containerStart: container.start, container: name, ...(repeat ? { repeat } : {}), records, kind: collection?.rule?.kind });
    }
    return { spans, repeats };
}
/** One binding per marker in `html`, plus one per unmarked `<img>` that still shows a review photo path. Ids are assigned by the caller. */
function fileBindings(file, scan, spans, content, used, unresolved, itemPage) {
    const { nodes, tags, invalidNodeIds } = scan;
    const stem = fileStem(file);
    const occurrences = [];
    const problem = (marker, reason) => unresolved.push({ file, marker, reason });
    const templateProblem = (marker, reason, container) => unresolved.push({ file, marker, reason, repeat: true, ...(container !== undefined ? { container } : {}) });
    for (const { tag, start, source } of tags) {
        const hasMarker = /\sdata-stackje-bind\s*=/.test(source);
        if (!hasMarker) {
            const src = decodeEntities(attributeValue(source, "src") ?? "");
            const recordId = REVIEW_MEDIA_PATH.exec(src)?.[1];
            const record = recordId ? content.recordsById[recordId] : undefined;
            if (!recordId || (record?.kind !== "media" && record?.kind !== "product"))
                continue;
            occurrences.push({ start, binding: { id: uniqueId(`${stem}-${recordId}`, used), file, kind: "image", recordId, ...(record.kind === "product" ? { field: "photo" } : {}) } });
            continue;
        }
        const value = attributeValue(source, "data-stackje-bind") ?? "";
        const marker = parseContentMarker(value);
        if (!marker) {
            problem(value, "not a content marker; write recordId.field, recordId.field@attribute or, on an <img>, recordId");
            continue;
        }
        if (marker.recordId === "group") {
            if (marker.field !== "label" || marker.attribute || tag === "img" || spans.some(span => start >= span.start && start < span.end)) {
                problem(value, "use group.label as text outside the repeat on a category or tag page");
                continue;
            }
            occurrences.push({ start, binding: { id: uniqueId(`${stem}-group-label`, used), file, kind: "text", recordId: GROUP_RECORD_ID, field: "label" } });
            continue;
        }
        if (marker.recordId === ITEM_MARKER) {
            const span = spans.find((candidate) => start >= candidate.start && start < candidate.end);
            if (!span && !itemPage) {
                templateProblem(value, "item markers belong inside the template of a data-stackje-repeat container or on an item page");
                continue;
            }
            if (!span && itemPage) {
                const field = marker.field;
                const kind = content.collections[itemPage]?.rule?.kind;
                if (!field || !KIND_FIELDS[kind]?.includes(field)) {
                    problem(value, `item page field ${field ?? ""} is not a ${kind} field`);
                    continue;
                }
                const base = { id: uniqueId(`${stem}-item-${field}`, used), file, recordId: TEMPLATE_RECORD_ID };
                if ((field === "body" || (kind === "product" && field === "description")) && !marker.attribute)
                    occurrences.push({ start, binding: { ...base, kind: "markdown", field } });
                else if (marker.attribute)
                    occurrences.push({ start, binding: { ...base, kind: "attribute", field, attribute: marker.attribute } });
                else if (tag === "img" && (field === "cover" || (kind === "product" && field === "photo")))
                    occurrences.push({ start, binding: { ...base, kind: "image", field } });
                else
                    occurrences.push({ start, binding: { ...base, kind: "text", field } });
                continue;
            }
            if (!span)
                continue;
            if (!span.repeat)
                continue;
            const field = marker.field;
            if (!field) {
                templateProblem(value, "an item marker needs a field: item.field", span.container);
                continue;
            }
            if (!everyKindHasField(span.records, field, span.kind))
                templateProblem(value, `${span.container} has members without a field ${field}; use fields every member has`, span.container);
            const base = { file, recordId: TEMPLATE_RECORD_ID, repeat: span.repeat.id };
            const id = (suffix) => uniqueId([span.repeat.id, ...suffix].join("-"), used);
            if (marker.attribute)
                occurrences.push({ start, binding: { ...base, id: id([field, marker.attribute]), kind: "attribute", field, attribute: marker.attribute } });
            else if (tag === "img") {
                if (field !== "image" && !(span.kind === "post" && field === "cover") && !(span.kind === "product" && field === "photo"))
                    templateProblem(value, span.kind === "post" ? "a post's photo is item.cover" : "a member's photo is item.image", span.container);
                occurrences.push({ start, binding: { ...base, id: id([field]), kind: "image", field } });
            }
            else
                occurrences.push({ start, binding: { ...base, id: id([field]), kind: "text", field } });
            continue;
        }
        const record = content.recordsById[marker.recordId];
        if (!record)
            problem(value, `record ${marker.recordId} does not exist`);
        const base = [stem, marker.recordId, marker.field, marker.attribute].filter(Boolean).join("-");
        if (marker.attribute) {
            if (!marker.field) {
                problem(value, "an attribute marker needs a field: recordId.field@attribute");
                continue;
            }
            if (record && !hasField(record, marker.field))
                problem(value, `record ${marker.recordId} has no field ${marker.field}`);
            occurrences.push({ start, binding: { id: uniqueId(base, used), file, kind: "attribute", recordId: marker.recordId, field: marker.field, attribute: marker.attribute } });
        }
        else if (tag === "img") {
            let field = marker.field;
            if (record?.kind === "media")
                field = undefined;
            else if (record?.kind === "product") {
                if (field !== "photo")
                    problem(value, `a product photo is ${marker.recordId}.photo`);
            }
            else if (record && isItemRecord(record)) {
                if (field !== "image")
                    problem(value, `an item's photo is ${marker.recordId}.image`);
            }
            else if (record)
                problem(value, `record ${marker.recordId} is not a photo or an item`);
            const imageBase = [stem, marker.recordId, field].filter(Boolean).join("-");
            occurrences.push({ start, binding: { id: uniqueId(imageBase, used), file, kind: "image", recordId: marker.recordId, ...(field ? { field } : {}) } });
        }
        else {
            if (!marker.field) {
                problem(value, "a text marker needs a field: recordId.field");
                continue;
            }
            if (record && !hasField(record, marker.field))
                problem(value, `record ${marker.recordId} has no field ${marker.field}`);
            occurrences.push({ start, binding: { id: uniqueId(base, used), file, kind: "text", recordId: marker.recordId, field: marker.field } });
        }
    }
    return { occurrences, nodes, invalidNodeIds };
}
function hasField(record, field) {
    return (record.kind === "product" && Boolean(KIND_FIELDS.product?.includes(field))) || Object.hasOwn(record.fields, field) || (isItemRecord(record) && (field === "image" || field === "url"));
}
/**
 * Marks lists: two or more sibling child nodes of one tag that each show records, all of one list kind
 * (or all members of one collection), become `card`s and their parent a `repeat`. Cards need not show
 * the same fields: an item without a photo is still an item. Returns the lists that show different
 * members on each card, which a template would write once.
 */
function markRepeats(facts, content, file) {
    const lists = [];
    const children = new Map();
    facts.forEach((fact, index) => {
        if (fact.element.parent >= 0)
            children.set(fact.element.parent, [...(children.get(fact.element.parent) ?? []), index]);
    });
    const collectionsOf = (recordId) => Object.entries(content.collections).filter(([, collection]) => collection.memberIds?.includes(recordId)).map(([id]) => id);
    for (const [parent, indexes] of children) {
        const groups = new Map();
        for (const index of indexes) {
            const fact = facts[index];
            if (!fact.bindings.length || fact.repeat)
                continue;
            groups.set(fact.element.tag, [...(groups.get(fact.element.tag) ?? []), index]);
        }
        const parentFact = facts[parent];
        // A nav's links, a header's items and so on keep their own types; only a plain region (or one typed `repeat`) lists cards.
        if (parentFact.explicit ? parentFact.explicit !== "repeat" : SEMANTIC_TAGS.has(parentFact.element.tag))
            continue;
        let found = false;
        for (const group of groups.values()) {
            if (group.length < 2)
                continue;
            const recordIds = group.map((index) => facts[index].bindings.map((binding) => binding.recordId));
            const kinds = new Set(recordIds.flat().map((id) => content.recordsById[id]?.kind));
            const sameKind = kinds.size === 1 && [...kinds].every((kind) => kind !== undefined && LIST_KINDS.has(kind));
            const sharedCollection = new Set(recordIds.flat()).size > 1 && collectionsOf(recordIds[0][0]).some((id) => recordIds.every((ids) => ids.every((recordId) => collectionsOf(recordId).includes(id))));
            if (!sameKind && !sharedCollection)
                continue;
            found = true;
            for (const index of group)
                if (!facts[index].explicit) {
                    facts[index].type = "card";
                    facts[index].guessed = false;
                }
            const shows = new Set(recordIds.map((ids) => [...new Set(ids)].sort().join(",")));
            const kind = [...kinds][0];
            if (shows.size > 1 && kind !== "media") {
                const collection = Object.keys(content.collections).find((id) => recordIds.every((ids) => ids.every((recordId) => content.collections[id].memberIds?.includes(recordId))));
                lists.push({ file, nodeId: parentFact.element.nodeId, cards: group.map((index) => facts[index].element.nodeId), ...(collection ? { collection } : {}), ...(kinds.size === 1 && kind ? { kind } : {}) });
            }
        }
        if (found && !parentFact.explicit) {
            parentFact.type = "repeat";
            parentFact.guessed = false;
        }
    }
    return lists;
}
/**
 * Builds the workspace manifest from a design's HTML (path to text) and the content it shows. Markers
 * in the HTML name their content (`recordId.field[@attribute]`), so nothing is declared twice.
 * `previous` keeps routes a rename decoupled from their file's name. `guessed` and `unresolved` say
 * what the designer should review; the manifest is complete either way.
 */
export function generateWorkspaceManifest(files, content, previous, options = {}) {
    const htmlFiles = Object.keys(files).filter(isHtml).sort((left, right) => left < right ? -1 : left > right ? 1 : 0);
    const guessed = [];
    const unresolved = [];
    const bindings = [];
    const nodes = [];
    const repeats = [];
    const handWrittenLists = [];
    const used = new Set();
    const usedRepeats = new Set();
    const itemPages = {};
    for (const file of htmlFiles) {
        const html = files[file];
        const bodyTag = /<body\b[^>]*>/i.exec(html)?.[0] ?? "";
        const itemPage = attributeValue(bodyTag, "data-stackje-item");
        if (itemPage) {
            if (!content.collections[itemPage]?.rule)
                unresolved.push({ file, reason: `item page collection ${itemPage} is not a rule collection` });
            else
                itemPages[file] = itemPage;
        }
        const scan = bundleMarkedElements(html);
        const page = fileRepeats(file, html, scan.repeats, content, usedRepeats, unresolved);
        repeats.push(...page.repeats);
        const found = fileBindings(file, scan, page.spans, content, used, unresolved, itemPage);
        for (const invalid of found.invalidNodeIds)
            unresolved.push({ file, nodeId: invalid, reason: "node ids start with a letter and use letters, digits, _ and - (at most 64)" });
        const seen = new Set();
        const facts = [];
        // Document order: the walker lists a node when its tag opens. A repeated id is skipped, keeping the first.
        const kept = new Map();
        found.nodes.forEach((element, index) => {
            if (seen.has(element.nodeId)) {
                unresolved.push({ file, nodeId: element.nodeId, reason: "node id is used more than once in this file" });
                return;
            }
            seen.add(element.nodeId);
            const typeAttribute = attributeValue(element.source, "data-stackje-type");
            const explicit = typeAttribute !== undefined && NODE_TYPES.includes(typeAttribute) ? typeAttribute : undefined;
            if (typeAttribute !== undefined && !explicit)
                unresolved.push({ file, nodeId: element.nodeId, reason: `unknown data-stackje-type ${typeAttribute}` });
            const tagType = TAG_TYPES[element.tag];
            // A template's nodes repeat with it; anywhere else a node never shows `@item`.
            const span = page.spans.find((candidate) => candidate.repeat && element.start >= candidate.start && element.start < candidate.end);
            const inside = found.occurrences.filter((occurrence) => occurrence.start >= element.start && occurrence.start < element.end && (span || !occurrence.binding.repeat)).map((occurrence) => occurrence.binding);
            kept.set(index, facts.length);
            facts.push({ element, ...(span ? { repeat: span.repeat.id } : {}), ...(explicit ? { explicit } : {}), type: explicit ?? tagType ?? "section", guessed: !explicit && !tagType, bindings: inside });
        });
        // A skipped duplicate leaves its children pointing at the wrong parent index; remap to the kept facts.
        for (const fact of facts) {
            let parent = fact.element.parent;
            while (parent >= 0 && !kept.has(parent))
                parent = found.nodes[parent].parent;
            fact.element = { ...fact.element, parent: parent >= 0 ? kept.get(parent) : -1 };
        }
        handWrittenLists.push(...markRepeats(facts, content, file));
        // A template list is declared, not guessed: its container is the `repeat` and its template the `card`.
        for (const span of page.spans) {
            if (!span.repeat)
                continue;
            for (const [start, type] of [[span.containerStart, "repeat"], [span.start, "card"]]) {
                const fact = facts.find((candidate) => candidate.element.start === start);
                if (fact && !fact.explicit) {
                    fact.type = type;
                    fact.guessed = false;
                }
            }
        }
        for (const fact of facts) {
            const recordIds = [...new Set(fact.bindings.map((binding) => binding.recordId))].slice(0, MAX_NODE_RECORDS);
            nodes.push({ id: fact.element.nodeId, file, type: fact.type, recordIds, ...(fact.repeat ? { repeat: fact.repeat } : {}) });
            if (fact.guessed)
                guessed.push({ file, nodeId: fact.element.nodeId, tag: fact.element.tag, type: fact.type });
            // A list's container shows its members through the template, so it has no records of its own to name.
            const holdsRepeat = page.spans.some((span) => span.containerStart >= fact.element.start && span.containerStart < fact.element.end);
            if (!recordIds.length && !holdsRepeat)
                unresolved.push({ file, nodeId: fact.element.nodeId, reason: "no bound records inside" });
        }
        bindings.push(...found.occurrences.map((occurrence) => occurrence.binding));
    }
    const routes = generateRoutes(htmlFiles, previous, unresolved, itemPages, content, options.shopWork, repeats.filter(repeat => repeat.collection === "products" && repeat.paginate).map(repeat => repeat.file));
    const unresolvedCurrent = unresolved.filter(item => !item.marker || !options.knownProducts?.has(parseContentMarker(item.marker)?.recordId ?? "") || !item.reason.endsWith("does not exist"));
    return { manifest: { schemaVersion: 1, rendererVersion: "bundle-v1", routes, bindings: bindings.map((binding) => (content.recordsById[binding.recordId]?.kind === "product" || (!content.recordsById[binding.recordId] && options.knownProducts?.has(binding.recordId))) ? { ...binding, product: true } : binding), nodes, ...(repeats.length ? { repeats } : {}), ...(Object.keys(itemPages).length ? { itemPages } : {}) }, guessed, unresolved: unresolvedCurrent, handWrittenLists };
}
/** The content marker that shows what a binding shows: the inverse of what `generateWorkspaceManifest` reads. */
export function contentMarker(binding) {
    const record = binding.recordId === TEMPLATE_RECORD_ID ? ITEM_MARKER : binding.recordId === GROUP_RECORD_ID ? "group" : binding.recordId;
    if (binding.kind === "attribute")
        return `${record}.${binding.field}@${binding.attribute}`;
    if (binding.kind === "image")
        return binding.field ? `${record}.${binding.field}` : record;
    return `${record}.${binding.field}`;
}
const REPEAT_ATTRIBUTE = /(\sdata-stackje-repeat\s*=\s*)(["'])([^"']*)\2/;
/** Edits to opening tags, applied back to front. Several edits to one tag compose. */
class TagEdits {
    edits = new Map();
    rewrite(start, source, change) {
        const edit = this.edits.get(start) ?? { from: source, to: source };
        edit.to = change(edit.to);
        this.edits.set(start, edit);
    }
    apply(html) {
        let output = html;
        for (const [at, edit] of [...this.edits].sort((left, right) => right[0] - left[0]))
            output = `${output.slice(0, at)}${edit.to}${output.slice(at + edit.from.length)}`;
        return output;
    }
}
/**
 * Rewrites each `data-stackje-bind="<binding id>"` in the HTML files to its content marker
 * (`recordId.field[@attribute]`; a media photo is its bare `recordId`, a template's is `item.field`)
 * using `bindings`, and each repeat container's id to its collection using `repeats`. Every other byte stays as it was. Generating a manifest from the result reproduces `bindings`, up to their ids.
 */
export function convertIdMarkersToContentMarkers(files, bindings, repeats = []) {
    const byId = new Map(bindings.map((binding) => [binding.id, binding]));
    const result = {};
    const unconverted = [];
    for (const [file, html] of Object.entries(files)) {
        if (!isHtml(file)) {
            result[file] = html;
            continue;
        }
        const edits = new TagEdits();
        const scan = bundleMarkedElements(html);
        for (const { tag, start, source } of scan.tags) {
            const id = attributeValue(source, "data-stackje-bind");
            if (id === undefined)
                continue;
            const binding = byId.get(id);
            if (!binding || binding.file !== file) {
                unconverted.push({ file, marker: id, reason: "no binding with this id for this file" });
                continue;
            }
            // `<img>` is how the generator tells an image from text, so the two must agree.
            if ((binding.kind === "image") !== (tag === "img") && binding.kind !== "attribute") {
                unconverted.push({ file, marker: id, reason: binding.kind === "image" ? "an image binding on a non-image element" : "a text binding on an <img>" });
                continue;
            }
            edits.rewrite(start, source, (tagSource) => tagSource.replace(/(\sdata-stackje-bind\s*=\s*)(["'])[^"']*\2/, (_whole, head, quote) => `${head}${quote}${contentMarker(binding)}${quote}`));
        }
        // A repeat's container names its collection in the workspace and its repeat id in the artifact.
        for (const container of scan.repeats) {
            const repeat = repeats.find((candidate) => candidate.id === container.repeatId && candidate.file === file);
            if (!repeat) {
                unconverted.push({ file, marker: container.repeatId, reason: "no repeat with this id for this file" });
                continue;
            }
            edits.rewrite(container.start, html.slice(container.start, container.openEnd), (tagSource) => tagSource.replace(REPEAT_ATTRIBUTE, (_whole, head, quote) => `${head}${quote}${repeat.collection}${quote}`));
        }
        result[file] = edits.apply(html);
    }
    return { files: result, unconverted };
}
/**
 * The inverse of `convertIdMarkersToContentMarkers`, for storing a design: rewrites each content
 * marker in the HTML files to the id of the binding `manifest` (from `generateWorkspaceManifest`)
 * made for it, and adds the id marker to an unmarked `<img>` that got an implicit photo binding.
 * Bindings are matched to markers in document order within each file, per marker. A repeat's
 * container takes the id of the repeat made for its collection, in document order too. Every other
 * byte stays as it was. A marker with no binding left (a malformed one, say) stays and is reported.
 */
export function contentMarkersToIds(files, manifest) {
    const result = {};
    const unconverted = [];
    for (const [file, html] of Object.entries(files)) {
        if (!isHtml(file)) {
            result[file] = html;
            continue;
        }
        const queues = new Map();
        for (const binding of manifest.bindings) {
            if (binding.file !== file)
                continue;
            const key = contentMarker(binding);
            queues.set(key, [...(queues.get(key) ?? []), binding]);
        }
        const edits = new TagEdits();
        const scan = bundleMarkedElements(html);
        for (const { tag, start, source } of scan.tags) {
            const value = attributeValue(source, "data-stackje-bind");
            if (value !== undefined) {
                // A media record's photo drops any field the marker wrote (`photo.image`), so its binding is the bare record.
                const parsed = parseContentMarker(value);
                const fallback = parsed && tag === "img" && parsed.field && !parsed.attribute ? queues.get(parsed.recordId) : undefined;
                const binding = queues.get(value)?.shift() ?? (fallback?.[0]?.kind === "image" ? fallback.shift() : undefined);
                if (!binding) {
                    unconverted.push({ file, marker: value, reason: "no binding was generated for this marker" });
                    continue;
                }
                edits.rewrite(start, source, (tagSource) => tagSource.replace(/(\sdata-stackje-bind\s*=\s*)(["'])[^"']*\2/, (_whole, head) => `${head}"${binding.id}"`));
                continue;
            }
            if (tag !== "img")
                continue;
            const recordId = REVIEW_MEDIA_PATH.exec(decodeEntities(attributeValue(source, "src") ?? ""))?.[1];
            const queue = recordId ? queues.get(recordId) ?? queues.get(`${recordId}.photo`) : undefined;
            if (!recordId || !queue?.length || queue[0].kind !== "image" || (queue[0].field && queue[0].field !== "photo"))
                continue;
            const binding = queue.shift();
            edits.rewrite(start, source, (tagSource) => `${tagSource.slice(0, "<img".length)} data-stackje-bind="${binding.id}"${tagSource.slice("<img".length)}`);
        }
        const pending = (manifest.repeats ?? []).filter((repeat) => repeat.file === file);
        for (const container of scan.repeats) {
            const index = pending.findIndex((repeat) => repeat.collection === container.repeatId);
            if (index < 0) {
                unconverted.push({ file, marker: container.repeatId, reason: "no repeat was generated for this container" });
                continue;
            }
            const [repeat] = pending.splice(index, 1);
            edits.rewrite(container.start, html.slice(container.start, container.openEnd), (tagSource) => tagSource.replace(REPEAT_ATTRIBUTE, (_whole, head, quote) => `${head}${quote}${repeat.id}${quote}`));
        }
        result[file] = edits.apply(html);
    }
    return { files: result, unconverted };
}
